import { createContext, useCallback, useContext, useEffect, useState } from "react"

import { get } from "@/shared/api"
import { API_ENDPOINTS } from "@/shared/api/endpoints"
import { toastError } from "@/shared/toast-error"

import { useAuth } from "@/contexts/auth-context"
import { useUser } from "@/contexts/user-context"
import { currencyService } from "@/shared/currencies/api"
import { budgetTermService } from "@/shared/budget-terms/api"
import type { Currency } from "@/shared/currencies/types"
import type { BudgetTerm } from "@/shared/budget-terms/types"
import { budgetItemCodeService } from "@/pages/budget-items/api"
import type { BudgetItemCode } from "@/pages/budget-items/api"
import type { OrgTypeAssignMethod, OrgIdAssignMethod } from "@/pages/budget-items/interfaces"

export interface TermMonth {
  monthIndex: number
  monthNo: number
  monthLabel: string
  yearNo: number
}

interface BudgetDataState {
  currencies: Currency[]
  budgetTerms: BudgetTerm[]
  budgetItemCodes: BudgetItemCode[]
  orgTypeAssignMethods: OrgTypeAssignMethod[]
  orgIdAssignMethods: OrgIdAssignMethod[]
  termMonths: Record<string, TermMonth[]>
  isLoading: boolean
  isItemCodesLoading: boolean
  refresh: () => Promise<void>
  refreshItemCodes: () => Promise<void>
  loadTermMonths: (budgetTerm: string) => Promise<TermMonth[]>
}

const BudgetDataContext = createContext<BudgetDataState | null>(null)

export function BudgetDataProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth()
  const { hasAccess, accessChecked } = useUser()
  const [currencies, setCurrencies] = useState<Currency[]>([])
  const [budgetTerms, setBudgetTerms] = useState<BudgetTerm[]>([])
  const [budgetItemCodes, setBudgetItemCodes] = useState<BudgetItemCode[]>([])
  const [orgTypeAssignMethods, setOrgTypeAssignMethods] = useState<OrgTypeAssignMethod[]>([])
  const [orgIdAssignMethods, setOrgIdAssignMethods] = useState<OrgIdAssignMethod[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isItemCodesLoading, setIsItemCodesLoading] = useState(false)
  const [termMonths, setTermMonths] = useState<Record<string, TermMonth[]>>({})

  const refresh = useCallback(async () => {
    setIsLoading(true)
    const [currencyResult, termResult, orgTypeResult, orgIdResult] = await Promise.allSettled([
      currencyService.index(),
      budgetTermService.index(),
      get<OrgTypeAssignMethod[]>(API_ENDPOINTS.RULE_ORG_TYPE_ASSIGN_METHODS),
      get<OrgIdAssignMethod[]>(API_ENDPOINTS.RULE_ORG_ID_ASSIGN_METHODS),
    ])
    if (currencyResult.status === "fulfilled") setCurrencies(currencyResult.value)
    else toastError("Para birimleri yüklenirken bir hata oluştu", currencyResult.reason)
    if (termResult.status === "fulfilled") setBudgetTerms(termResult.value)
    else toastError("Bütçe dönemleri yüklenirken bir hata oluştu", termResult.reason)
    if (orgTypeResult.status === "fulfilled") {
      setOrgTypeAssignMethods(Array.isArray(orgTypeResult.value.data) ? orgTypeResult.value.data : [])
    } else {
      toastError("Organizasyon tipi atama metotları yüklenirken bir hata oluştu", orgTypeResult.reason)
    }
    if (orgIdResult.status === "fulfilled") {
      setOrgIdAssignMethods(Array.isArray(orgIdResult.value.data) ? orgIdResult.value.data : [])
    } else {
      toastError("Organizasyon atama metotları yüklenirken bir hata oluştu", orgIdResult.reason)
    }
    setIsLoading(false)
  }, [])

  const loadTermMonths = useCallback(
    async (budgetTerm: string): Promise<TermMonth[]> => {
      if (!budgetTerm) return []
      const cached = termMonths[budgetTerm]
      if (cached) return cached
      try {
        const res = await get<TermMonth[]>(API_ENDPOINTS.BUDGET_PLANS_TERM_MONTHS, {
          params: { budgetTerm },
        })
        const data = res.data
        const list = Array.isArray(data) ? data : (data as { value?: TermMonth[] })?.value ?? []
        setTermMonths((prev) => ({ ...prev, [budgetTerm]: list }))
        return list
      } catch (err) {
        toastError("Bütçe ayları yüklenirken bir hata oluştu", err)
        return []
      }
    },
    [termMonths],
  )

  const refreshItemCodes = useCallback(async () => {
    setIsItemCodesLoading(true)
    try {
      const data = await budgetItemCodeService.index()
      setBudgetItemCodes(data)
    } catch (err) {
      toastError("Kalem kodları yüklenirken bir hata oluştu", err)
    } finally {
      setIsItemCodesLoading(false)
    }
  }, [])

  useEffect(() => {
    if (isAuthenticated && accessChecked && hasAccess) {
      refresh()
      refreshItemCodes()
    }
  }, [isAuthenticated, accessChecked, hasAccess, refresh, refreshItemCodes])

  return (
    <BudgetDataContext.Provider value={{ currencies, budgetTerms, budgetItemCodes, orgTypeAssignMethods, orgIdAssignMethods, termMonths, isLoading, isItemCodesLoading, refresh, refreshItemCodes, loadTermMonths }}>
      {children}
    </BudgetDataContext.Provider>
  )
}

export function useBudgetData(): BudgetDataState {
  const ctx = useContext(BudgetDataContext)
  if (!ctx) throw new Error("useBudgetData must be used within BudgetDataProvider")
  return ctx
}
